import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "../common/Button";
import AppContext from "../../AppContext";
import useUsers from "../../hooks/useUsers";
import { Form, Formik } from "formik";
import TextArea from "../common/TextArea";
import { fromNow } from "../../helpers/common.helper";
import * as Tooltip from "react-tooltip";

import Rating from "@mui/material/Rating";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import RatingStar from "../common/Rating";
import { ReactComponent as EditSvg } from "../../assets/workExchange/icon-edit.svg";

const Review = ({ workId, workCreator, onReviewed }) => {
  const { user, company } = useContext(AppContext);
  const [review, setReview] = useState(undefined);
  const { queryUserRatings, createRatings, queryUsers, patchRating } =
    useUsers();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, []);

  const fetchData = async () => {
    const { data: workRatings } = await queryUserRatings(
      {
        work: workId,
        ratedBy: user.company ?? user._id,
      },
      1,
      1
    );

    if (workRatings.length === 0) {
      return;
    }

    const [_review] = workRatings;

    // * Reviewer
    _review.ratedBy = user.company
      ? {
          name: company.name,
          avatar: company.logo,
        }
      : {
          name: `${user.firstname} ${user.surname ?? ""}`,
          avatar: user.avatar,
        };

    setReview(_review);
  };

  const handleValidate = (values) => {
    const { review, score } = values;

    let errors = {};

    if (!review) errors.review = "Cannot leave empty";
    if (+score === 0) errors.score = "Please leave a score";

    return errors;
  };

  const handleSubmitReview = async (values, { setSubmitting }) => {
    setSubmitting(true);

    // * Edit
    if (review) {
      const { data } = await patchRating(review._id, {
        rate: +values.score,
        msg: values.review,
      });

      // * Set new review data to state, but keep ratedBy value
      setReview((curr) => ({ ...data, ratedBy: curr.ratedBy }));
      setIsEditing(false);
      setSubmitting(false);

      onReviewed();
      return;
    }

    // * Create new Rating
    const { data } = await createRatings({
      work: workId,
      msg: values.review,
      rate: +values.score,
      for: workCreator,
      ratedBy: user.company ?? user._id,
    });

    setReview({
      ...data,
      ratedBy: user.company
        ? {
            name: company.name,
            avatar: company.logo,
          }
        : {
            name: `${user.firstname} ${user.surname ?? ""}`,
            avatar: user.avatar,
          },
    });
    setIsEditing(false);
    setSubmitting(false);

    onReviewed();
  };

  // * Render nothing if not logged in
  if (!user) {
    return null;
  }

  // * Render edit or create
  if (isEditing || !review) {
    return (
      <div className="project-view-review">
        <div className="project-view-review__heading">
          {!review ? "Submit your review" : "Update your review"}
        </div>
        <Formik
          initialValues={{
            review: review?.msg ?? "",
            score: review?.rate ?? 0,
          }}
          enableReinitialize={true}
          validate={handleValidate}
          onSubmit={handleSubmitReview}
          isInitialValid={false}
        >
          {({ values, isValid, setFieldValue, isSubmitting }) => (
            <Form className="project-view-review-form">
              <TextArea name="review" maxLength={100} />
              <div className="project-view-review-form__footer">
                <Rating
                  value={values.score}
                  precision={0.5}
                  defaultValue={values.score}
                  icon={<StarRoundedIcon fontSize="large" />}
                  emptyIcon={<StarBorderRoundedIcon fontSize="large" />}
                  onChange={(event, newValue) => {
                    setFieldValue("score", newValue);
                  }}
                />
                <Button
                  disabled={!isValid}
                  label={review ? "Update Review" : "Submit Review"}
                  type="submit"
                  submitting={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }

  if (!review) {
    return null;
  }

  return (
    <>
      <div className="project-view-review__heading--my-review">
        Update your review
      </div>
      <div className="project-view-reviews-content">
        <div className="project-view-reviews-content-wrp">
          <RatingStar score={review?.rate} size={24} readOnly />
          <div className="project-view-reviews-content__name">{`${review?.ratedBy.name}`}</div>
          <div className="project-view-reviews-content__update">
            {fromNow(review.createdOn)}
          </div>
          <div className="project-view-reviews-content__my-review">
            {review.msg}
          </div>
        </div>
        <div
          className="project-view-reviews-content__edit"
          onClick={() => setIsEditing(true)}
          data-tooltip-id="tooltip-review"
          data-tooltip-content="Edit"
          data-tooltip-place="top"
        >
          <EditSvg />
        </div>
        <Tooltip.Tooltip id="tooltip-review" />
      </div>
    </>
  );
};

Review.propTypes = {};

export default Review;
